package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.team.AnnouncementDto
import com.evoapps.evoshift.data.dto.team.AnnouncementReadDto
import com.evoapps.evoshift.data.dto.team.ShiftNoteDto
import com.evoapps.evoshift.data.mapper.AnnouncementMapper
import com.evoapps.evoshift.domain.model.Announcement
import com.evoapps.evoshift.domain.model.AnnouncementRead
import com.evoapps.evoshift.domain.model.ShiftNote
import com.evoapps.evoshift.domain.repository.AnnouncementRepository
import com.evoapps.evoshift.domain.repository.ShiftNoteRepository
import dev.gitlive.firebase.firestore.Direction
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map

/**
 * SH-118 team announcements in Firestore: `workplaces/{wpId}/announcements/{id}` with the read
 * receipts in `reads/{uid}`. functions/announcements.js sends each new post to the inboxes,
 * counts its recipients and sets its 12-month TTL; firestore.rules decide who reads and writes.
 */
class AnnouncementRepositoryImpl(private val firestore: AppFirestore) : AnnouncementRepository {

    private fun posts(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("announcements")

    private fun receipt(workplaceId: String, announcementId: String, userId: String) =
        posts(workplaceId).document(announcementId).collection("reads").document(userId)

    override fun observe(workplaceId: String, limit: Int): Flow<List<Announcement>> =
        posts(workplaceId)
            .orderBy("createdAt", Direction.DESCENDING)
            .limit(limit)
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents.mapNotNull { doc ->
                    runCatching { AnnouncementMapper.toDomain(doc.data<AnnouncementDto>(), doc.id, workplaceId) }.getOrNull()
                }
            }
            .distinctUntilChanged()

    override suspend fun post(announcement: Announcement): Result<String> = runCatching {
        val ref = if (announcement.id.isBlank()) posts(announcement.workplaceId).document
        else posts(announcement.workplaceId).document(announcement.id)
        ref.set(AnnouncementMapper.toNewDto(announcement, ref.id))
        ref.id
    }

    override suspend fun setPinned(workplaceId: String, announcementId: String, pinned: Boolean, editorId: String): Result<Unit> =
        runCatching {
            posts(workplaceId).document(announcementId).updateFields {
                "pinned" to pinned
                "updatedAt" to AppClock.now().toString()
                "updatedBy" to editorId
            }
        }

    override suspend fun delete(workplaceId: String, announcementId: String): Result<Unit> = runCatching {
        posts(workplaceId).document(announcementId).delete()
    }

    override suspend fun readIds(workplaceId: String, announcementIds: Collection<String>, userId: String): Result<Set<String>> =
        runCatching {
            coroutineScope {
                announcementIds.distinct()
                    .map { id -> async { id.takeIf { receipt(workplaceId, id, userId).get().exists } } }
                    .awaitAll()
                    .filterNotNull()
                    .toSet()
            }
        }

    override suspend fun markRead(workplaceId: String, announcementId: String, userId: String, readAt: String): Result<Unit> =
        runCatching {
            val ref = receipt(workplaceId, announcementId, userId)
            // Once: the rules refuse overwriting the first time.
            if (!ref.get().exists) ref.set(AnnouncementReadDto(userId = userId, readAt = readAt))
        }

    override suspend fun reads(workplaceId: String, announcementId: String): Result<List<AnnouncementRead>> = runCatching {
        posts(workplaceId).document(announcementId).collection("reads").get().documents
            .map { AnnouncementMapper.toDomain(it.data<AnnouncementReadDto>(), it.id) }
    }
}

/** SH-118 shift notes in Firestore: `workplaces/{wpId}/shift_notes/{shiftId}`. */
class ShiftNoteRepositoryImpl(private val firestore: AppFirestore) : ShiftNoteRepository {

    private fun notes(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("shift_notes")

    override suspend fun get(workplaceId: String, shiftId: String): Result<ShiftNote?> = runCatching {
        notes(workplaceId).document(shiftId).get().takeIf { it.exists }
            ?.let { AnnouncementMapper.toDomain(it.data<ShiftNoteDto>(), it.id, workplaceId) }
    }

    override fun observeFor(workplaceId: String, userId: String): Flow<List<ShiftNote>> =
        notes(workplaceId)
            .where { "userId" equalTo userId }
            .safeSnapshots
            .map { snapshot -> snapshot.documents.map { AnnouncementMapper.toDomain(it.data<ShiftNoteDto>(), it.id, workplaceId) } }
            .distinctUntilChanged()

    override suspend fun save(note: ShiftNote): Result<Unit> = runCatching {
        notes(note.workplaceId).document(note.shiftId).set(AnnouncementMapper.toDto(note))
    }

    override suspend fun delete(workplaceId: String, shiftId: String): Result<Unit> = runCatching {
        notes(workplaceId).document(shiftId).delete()
    }
}
